import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { announceCartLinesUpdate, settleWithFetchedCart, announceCartError } from '@theme/standard-cart-events';
import { morph } from '@theme/morph';
import { prepareProductForm, productScope, cartError, fetchProduct, clearProductCache } from '@theme/variant-selection';

const submittingScopes = new WeakSet();

class ProductFormComponent extends Component {
  requiredRefs = ['liveRegion'];
  #eventTarget = null;
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget = productScope(this);
    this.#eventTarget.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    if (this.dataset.productUrl && this.dataset.productId) fetchProduct(this.dataset.productUrl, this.dataset.productId).catch(() => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  async handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    const scope = productScope(this);
    if (this.#submitting || submittingScopes.has(scope)) return;
    this.#submitting = true;
    submittingScopes.add(scope);
    this.setAttribute('aria-busy', 'true');
    this.addToCartButton?.classList.add('btn--loading');
    this.refs.addToCartError?.classList.add('hidden');
    let announcement;
    try {
      const prepared = await prepareProductForm(this);
      if (!prepared) return;
      const { data: formData, variant } = prepared;
      const durationFields = this.querySelector('[data-hire-options]') || scope.querySelector('.buy-buttons-block [data-hire-options]');
      if (durationFields && !window.OlouiooDuration) throw new Error('Please reload the page to load your hire duration.');
      const bookingItems = window.OlouiooDuration?.productItems(this, formData);
      formData.set('sections', [...new Set(Array.from(document.querySelectorAll('cart-items-component')).map(item => item.dataset.sectionId).filter(Boolean))].slice(0, 5).join(','));
      formData.set('sections_url', window.location.pathname);
      announcement = announceCartLinesUpdate(this, {
        action: 'add', context: 'product',
        lines: bookingItems ? bookingItems.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })) : [{ merchandiseId: String(variant.id), quantity: Number(formData.get('quantity')) }],
      });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body: bookingItems ? JSON.stringify({ items: bookingItems, sections: formData.get('sections').split(',').filter(Boolean), sections_url: window.location.pathname }) : formData }),
        headers: { Accept: 'application/json', ...(bookingItems ? { 'Content-Type': 'application/json' } : {}) },
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) throw new Error(cartError(result));
      this.refs.liveRegion.textContent = '';
      document.dispatchEvent(new CartAddEvent({}, String(variant.id), {
        source: 'product-form-component', productId: this.dataset.productId, sections: result.sections,
      }));
      settleWithFetchedCart(announcement, { source: 'product-form-component', sourceId: this.id, productId: this.dataset.productId, sections: result.sections });
    } catch (error) {
      clearProductCache();
      const message = error.message || String(error);
      announcement?.reject(error);
      announceCartError(this, { error: message, code: 'INVALID' });
      window.dispatchEvent(new CartErrorEvent(this.id, message));
      this.refs.addToCartError?.classList.remove('hidden');
      if (this.refs.addToCartTextError) this.refs.addToCartTextError.textContent = message;
      this.refs.liveRegion.textContent = message;
    } finally {
      this.#submitting = false;
      submittingScopes.delete(scope);
      this.removeAttribute('aria-busy');
      this.addToCartButton?.classList.remove('btn--loading');
    }
  }

  #onVariantUpdate = (event) => {
    const detail = event.detail;
    const data = detail?.data;
    if (!data || String(data.productId) !== String(this.dataset.productId)) return;
    if (productScope(event.target) !== productScope(this)) return;
    const variant = detail.resource;
    if (variant === undefined || (variant !== null && (variant.available !== true && variant.available !== false))) return;
    if (data.newProduct) {
      this.dataset.productId = String(data.newProduct.id);
      this.dataset.productUrl = data.newProduct.url;
    }
    const available = Boolean(variant?.id) && variant.available === true;
    const soldOut = Boolean(variant?.id) && variant.available === false;
    if (this.refs.variantId) this.refs.variantId.value = variant?.id ? String(variant.id) : '';
    let button = this.addToCartButton;
    if (!button) return;
    const source = Array.from(data.html?.querySelectorAll('product-form-component') || [])
      .find(component => component.dataset.productId === this.dataset.productId && Boolean(component.closest('sticky-add-to-cart')) === Boolean(this.closest('sticky-add-to-cart')));
    const newButton = source?.querySelector('[ref="addToCartButton"]');
    if (newButton) morph(button, newButton);
    button = this.querySelector('[ref="addToCartButton"]') || this.addToCartButton;
    if (!button) return;
    button.toggleAttribute('disabled', !available);
    if (!newButton) {
      const label = available ? this.dataset.addLabel : soldOut ? this.dataset.soldOutLabel : this.dataset.unavailableLabel;
      const text = button.querySelector('[ref="addToCartButtonText"], .add-to-cart-text');
      if (label && text) text.textContent = label;
    }
    this.refs.acceleratedCheckoutButtonContainer?.toggleAttribute('hidden', !available);
    this.classList.toggle('show-back-in-stock', soldOut);
  };

  get addToCartButton() { return this.refs.addToCartButton; }
  get mediaGallery() { return this.querySelector('media-gallery'); }
}
if (!customElements.get('product-form-component')) customElements.define('product-form-component', ProductFormComponent);

